import { FileText, Mail } from "lucide-react";
import { sourceAnchorId } from "@/lib/chat-citations";
import { formatDate } from "@/lib/utils";
import type { ChatSourceOut } from "@/hooks/use-chat";

interface ChatMessageSourcesProps {
  sources: ChatSourceOut[];
  // Id del mensaje al que pertenece esta lista, para producir anchors
  // únicos `id="source-N-<msg>"` que el chip clickeable del cuerpo enlaza.
  messageId: string;
}

export default function ChatMessageSources({
  sources,
  messageId,
}: ChatMessageSourcesProps) {
  if (!sources || sources.length === 0) return null;

  return (
    <details className="mt-3 pt-3 border-t border-line group">
      <summary className="text-[11px] uppercase tracking-wider font-heading font-semibold text-content-muted mb-2 cursor-pointer select-none hover:text-ink list-none flex items-center gap-1">
        <span className="transition-transform group-open:rotate-90">▶</span>
        Ver fuentes ({sources.length})
      </summary>
      <ol className="space-y-2 list-none p-0 m-0">
        {sources.map((source, idx) => {
          const num = idx + 1;
          const anchorId = sourceAnchorId(num, messageId);
          const isEmail = !!source.email_meta;
          // Fuente colgante: un mensaje viejo puede citar un chunk cuyo
          // documento ya fue borrado. El backend deja `filename` vacío en
          // esos casos. La renderizamos atenuada y sin enlaces/preview.
          const isDeleted = !source.filename;

          if (isDeleted) {
            return (
              <li
                id={anchorId}
                key={source.chunk_id ?? anchorId}
                className="text-xs bg-surface p-2.5 rounded border border-line border-dashed opacity-60"
              >
                <div className="flex items-baseline gap-2">
                  <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-pill text-[10px] font-heading font-semibold bg-surface-200 text-content-muted flex-shrink-0">
                    {num}
                  </span>
                  <p className="font-heading font-medium text-content-muted italic flex items-center gap-1">
                    <FileText className="w-3 h-3 flex-shrink-0 text-content-muted" />
                    <span>Fuente eliminada</span>
                  </p>
                </div>
              </li>
            );
          }

          return (
            <li
              id={anchorId}
              key={source.chunk_id ?? anchorId}
              className="text-xs bg-surface p-2.5 rounded border border-line transition-shadow"
            >
              <div className="flex items-baseline gap-2 mb-1">
                <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-pill text-[10px] font-heading font-semibold bg-brand text-white flex-shrink-0">
                  {num}
                </span>
                <p className="font-heading font-semibold text-ink truncate flex items-center gap-1">
                  {isEmail ? (
                    <Mail className="w-3 h-3 flex-shrink-0 text-content-muted" />
                  ) : (
                    <FileText className="w-3 h-3 flex-shrink-0 text-content-muted" />
                  )}
                  <span className="truncate">{source.filename}</span>
                  {source.page != null && (
                    <span className="text-content-muted font-normal">
                      {" "}
                      · pág. {source.page}
                    </span>
                  )}
                </p>
              </div>
              {isEmail && (
                <p className="text-content-muted text-[11px] truncate">
                  {source.email_meta?.sender_email && (
                    <span>De: {source.email_meta.sender_email}</span>
                  )}
                  {source.email_meta?.date && (
                    <span> · {formatDate(source.email_meta.date)}</span>
                  )}
                  {source.email_meta?.subject && (
                    <span> · {source.email_meta.subject}</span>
                  )}
                </p>
              )}
              {(source.cited_text || source.content_preview) && (
                <p className="text-content-muted mt-1 line-clamp-3">
                  {source.cited_text || source.content_preview}
                </p>
              )}
              {typeof source.score === "number" && (
                <p className="text-content-muted/70 mt-1 text-[10px]">
                  Relevancia {Math.round(source.score * 100)}%
                </p>
              )}
            </li>
          );
        })}
      </ol>
    </details>
  );
}
